import { useGetOpenTools } from '@/modules/gpt-chats/hooks/use-tools';
import { toolsService } from '@/modules/gpt-chats/services/tools.service';
import { Card, CardContent } from '@/components/ui-kit/card';
import { cn } from '@/lib/utils';
import Link from '@/components/core/router-link';
import { useState } from 'react';
import { ToolsCard } from '@/modules/gpt-chats/components/tools/tool-card';
import { useQueryClient } from '@tanstack/react-query';
import { Pagination } from '@/components/ui-kit/pagination';
import { ToolCardData } from '@/modules/gpt-chats/types/tools.service.type';
import { Wrench } from 'lucide-react';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { ToolDetailsModal } from './components/tool-details-modal';
import { useTranslation } from 'react-i18next';

import { ToolsListSkelton } from './tools-list';

const NoBlocksTool = ({
  isLoading = false,
  isAdmin = false,
}: {
  isLoading?: boolean;
  isAdmin?: boolean;
}) => {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <Card className="border-none p-0 shadow-none">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Skeleton className="mb-6 h-20 w-20 rounded-full" />
          <Skeleton className="mb-2 h-7 w-40" />
          <Skeleton className="mb-2 h-5 w-80" />
          <Skeleton className="mb-8 h-5 w-72" />
          <Skeleton className="h-10 w-28 rounded-md" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none p-0 shadow-none">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
          <Wrench className="h-10 w-10 text-muted-foreground" />
        </div>

        <h3 className="mb-2 text-xl font-semibold">{t('NO_BLOCKS_TOOLS')}</h3>

        <p className="mb-8 text-center text-base leading-relaxed text-muted-foreground">
          {isAdmin ? t('NO_BLOCKS_TOOLS_ADMIN_DESCRIPTION') : t('NO_BLOCKS_TOOLS_DESCRIPTION')}
        </p>
      </CardContent>
    </Card>
  );
};

export const BlocksToolsList = ({
  offset,
  limit,
  view,
  toolType,
  toolStatus,
  onOffsetChange,
  onLimitChange,
}: {
  offset: number;
  limit: number;
  view: string | undefined;
  toolType?: string | null;
  toolStatus?: string | null;
  onOffsetChange: (offset: number) => void;
  onLimitChange?: (limit: number) => void;
}) => {
  const queryClient = useQueryClient();
  const isAdmin = false;
  const { t } = useTranslation();

  const [selectedToolId, setSelectedToolId] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useGetOpenTools({
    tool_type: toolType || null,
    tool_status: toolStatus || null,
    page: offset + 1,
    page_size: limit,
  });

  const tools = data?.tools || [];

  const handleToolClick = async (toolId: string) => {
    if (!isAdmin) {
      setSelectedToolId(toolId);
    } else {
      await queryClient.prefetchQuery({
        queryKey: ['tools', 'open-tool-details', toolId],
        queryFn: () => toolsService.getOpenToolDetails(toolId),
      });
    }
  };

  const renderToolsGrid = (toolList: typeof tools) => (
    <div
      className={cn(
        'grid',
        view == 'grid' && 'grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3',
        view == 'list' && 'gap-1.5',
        toolList.length > 0 && view == 'grid' && 'min-h-[140px]',
        toolList.length > 0 && view == 'list' && 'min-h-[180px]',
        toolList.length > 0 && toolList.length <= 2 && view == 'grid' && 'auto-rows-auto',
        toolList.length > 2 && view == 'grid' && 'auto-rows-fr',
        view == 'list' && 'auto-rows-auto'
      )}
    >
      {toolList.map((tool, index: number) => {
        const detailsPath =
          tool.type === 'mcp_server'
            ? `/ai/mcp-server-details/${tool.id}/configuration?isBlocksTool=true&copiedBlocksTool=false`
            : `/ai/tool-details/${tool.id}/authentication?isBlocksTool=true&copiedBlocksTool=false`;

        const mappedTool: ToolCardData = {
          id: tool.id,
          name: tool.name,
          description: tool.description || '',
          status: tool.status,
          toolType: tool.type,
          is_open_tool: true,
        };

        return !isAdmin ? (
          <div key={index} className="cursor-pointer" onClick={() => handleToolClick(tool.id)}>
            <ToolsCard tool={mappedTool} view={view as 'list' | 'grid'} copiedBlocksTool={false} />
          </div>
        ) : (
          <Link href={detailsPath} key={index} onClick={() => handleToolClick(tool.id)}>
            <ToolsCard tool={mappedTool} view={view as 'list' | 'grid'} copiedBlocksTool={false} />
          </Link>
        );
      })}
    </div>
  );

  const isNoTools = !isLoading && !isFetching && data && data.total_count === 0;

  return (
    <>
      {isNoTools ? (
        <NoBlocksTool isAdmin={isAdmin} />
      ) : (
        <Card>
          <CardContent>
            {(isLoading || isFetching) && (
              <div
                className={cn(
                  'grid',
                  view == 'grid' && 'grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3',
                  view == 'list' && 'gap-1.5'
                )}
              >
                <ToolsListSkelton view={view ?? 'list'} />
              </div>
            )}
            {!isFetching && renderToolsGrid(tools)}
            {data && data.total_count > 0 && (
              <div className="mt-5 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('SHOWING_ENTRIES', {
                    from: offset * limit + 1,
                    to: Math.min((offset + 1) * limit, data.total_count),
                    total: data.total_count,
                  })}
                </span>
                {data.total_count > limit && (
                  <Pagination
                    page={offset}
                    onChange={onOffsetChange}
                    onPageSizeChange={onLimitChange}
                    totalCount={data.total_count}
                    pageSizeOptions={[10, 50, 100]}
                    pageSize={limit}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <ToolDetailsModal toolId={selectedToolId} onClose={() => setSelectedToolId(null)} />
    </>
  );
};
